import React, { useRef } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import ContextMenu, { ContextMenuDivider, ContextMenuItem } from "../common/ContextMenu.jsx"

/**
 * @param {{
 *   label: React.ReactNode,
 *   sortKey: string,
 *   sortBy?: string,
 *   sortDirection?: "asc" | "desc",
 *   sortable?: boolean,
 *   filterable?: boolean,
 *   isFilterActive?: boolean,
 *   sortAscLabel?: string,
 *   sortDescLabel?: string,
 *   isOpen?: boolean,
 *   onOpenChange?: (nextOpen: boolean) => void,
 *   onSort?: (sortKey: string, direction: "asc" | "desc" | null) => void,
 *   onFilter?: () => void,
 * }} props
 */
export default function TableHeaderMenu({
    label,
    sortKey,
    sortBy = "",
    sortDirection = "desc",
    sortable = false,
    filterable = false,
    isFilterActive = false,
    sortAscLabel = "Sort A-Z",
    sortDescLabel = "Sort Z-A",
    isOpen = false,
    onOpenChange = () => {},
    onSort = () => {},
    onFilter = () => {},
}) {
    const anchorRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const isSorted = sortBy === sortKey
    const hasMenu = sortable || filterable
    const sortIcon = isSorted && sortDirection === "asc" ?
        <ChevronUp size={14} /> :
        <ChevronDown size={14} />

    function handleSort(direction) {
        if (isSorted && sortDirection === direction) {
            onSort(sortKey, null)
            return
        }
        onSort(sortKey, direction)
    }

    function handleFilter() {
        onFilter()
    }

    const sharedHeaderStyle = {
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "4px",
        lineHeight: 1.2,
        overflow: "hidden",
    }

    if (!hasMenu) {
        return (
            <div style={sharedHeaderStyle}>
                <span className="text-truncate" style={{ minWidth: 0, width: "100%" }}>
                    {label}
                </span>
            </div>
        )
    }

    return (
        <>
            <div ref={anchorRef} className="position-relative w-100" style={{ minWidth: 0 }}>
                <button
                    type="button"
                    className="text-start text-reset text-decoration-none w-100"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onClick={() => onOpenChange(!isOpen)}
                    style={{
                        ...sharedHeaderStyle,
                        border: "none",
                        background: "transparent",
                        padding: 0,
                        cursor: "pointer",
                        textTransform: "inherit",
                        fontSize: "inherit",
                        fontWeight: "inherit",
                        lineHeight: "inherit",
                        color: "inherit",
                    }}
                >
                    <span className="text-truncate" style={{ minWidth: 0 }}>
                        {label}
                    </span>
                    <span
                        style={{
                            width: "14px",
                            flex: "0 0 14px",
                            lineHeight: 0,
                            visibility: isSorted ? "visible" : "hidden",
                        }}
                    >
                        {sortIcon}
                    </span>
                </button>
            </div>
            <ContextMenu
                open={isOpen}
                onOpenChange={onOpenChange}
                anchorRef={anchorRef}
                repositionDeps={[sortable, filterable, sortAscLabel, sortDescLabel, label]}
            >
                {sortable ? (
                    <>
                        <ContextMenuItem
                            itemKey="sort-asc"
                            exclusiveGroup="sort"
                            clearOnReselect
                            active={isSorted && sortDirection === "asc"}
                            onClick={() => handleSort("asc")}
                        >
                            {sortAscLabel}
                        </ContextMenuItem>
                        <ContextMenuItem
                            itemKey="sort-desc"
                            exclusiveGroup="sort"
                            clearOnReselect
                            active={isSorted && sortDirection === "desc"}
                            onClick={() => handleSort("desc")}
                        >
                            {sortDescLabel}
                        </ContextMenuItem>
                    </>
                ) : null}
                {sortable && filterable ? <ContextMenuDivider /> : null}
                {filterable ? (
                    <ContextMenuItem
                        itemKey="filter"
                        active={isFilterActive}
                        onClick={handleFilter}
                    >
                        Filter...
                    </ContextMenuItem>
                ) : null}
            </ContextMenu>
        </>
    )
}
